/**
 * A4 print CSS for the Destiny Blueprint / chart-analysis PDF path (Puppeteer).
 *
 * Inject via `<style>{DESTINY_BLUEPRINT_PDF_STYLES}</style>` on `PrintResult`.
 * Styles are scoped under `[data-db-pdf-root]` / `.print-root` so they do not
 * bleed into Monthly Consultation or Alignment Advantage print stacks.
 *
 * ## Class / attribute hooks (Agents 2–5)
 *
 * | Hook | Role |
 * |------|------|
 * | `.print-avoid-break` | Keep-together unit (`break-inside: avoid`) — prefer card-sized blocks |
 * | `[data-pdf-keep-together]` | Same as `.print-avoid-break` (attribute form for components) |
 * | `.print-page-break` | Force a page break before the element |
 * | `[data-pdf-page-break-before]` | Same as `.print-page-break` (existing attribute contract) |
 * | `[data-pdf-section-opener]` | Section banner — avoid mid-banner cuts; prefer keep-with-next |
 * | `[data-pdf-chapter]` | Multi-page chapter shell — allow breaks inside (never avoid whole chapter) |
 * | `.print-hide` / `[data-pdf-print-hide]` | Hide screen-only chrome in print/PDF |
 * | `[data-pdf-decorative-watermark]` | Huge background glyphs / numbers — hidden in print (audit #4 / #12) |
 * | `[data-db-pdf-emphasis]` | Inline phrase emphasis — solid ink (no clip-text) for PDF (audit #1 / #11) |
 * | `[data-db-pdf-grid="pair"]` | Equal-height 2-col pair (stretch children) |
 * | `[data-db-pdf-grid="cards-2"]` | 2-col card grid; odd last child spans full width (no dead corner) |
 * | `[data-db-pdf-card]` | Print card surface — flat border, no drop shadow |
 * | `[data-db-pdf-top-badge]` | Shared TOP / dominant badge chrome |
 * | `.db-pdf-type-*` | Typography roles (section / card-title / body / label / eyebrow) |
 * | `.db-pdf-running-header` / `.db-pdf-running-footer` | Screen preview chrome; Puppeteer owns PDF page numbers |
 * | `[data-db-pdf-cjk]` | Zodiac / Han glyphs — embedded Noto Sans TC subset (audit #3) |
 *
 * Puppeteer `page.pdf()` for `/print/result/` uses matching margins +
 * `displayHeaderFooter` for brand + page numbers. `@page` margins MUST reserve
 * the same top/bottom band — with `@page { margin: 0 }`, Chromium lays content
 * full-bleed and running chrome paints on top of page-start cards (collision).
 *
 * Do NOT set `overflow: hidden` on `html` / `body` / `[data-db-pdf-root]` —
 * that creates a fragmentation root and Chromium may drop pages after the first.
 */

/** Matches `DESTINY_PDF_MARGINS` in `server/src/pdf/renderPdf.ts` (header clearance). */
const DESTINY_PDF_PAGE_MARGIN_CSS = "20mm 12mm 16mm 12mm";

/**
 * Public URL for the Destiny Blueprint zodiac CJK subset (12 animals + 未知).
 * Self-hosted so Puppeteer does not depend on Google Fonts at render time.
 */
export const DESTINY_PDF_CJK_FONT_URL = "/fonts/NotoSansTC-ZodiacSubset.woff2";

/**
 * Full CSS string for Destiny Blueprint PDF print. Safe to inject as a style tag.
 */
export const DESTINY_BLUEPRINT_PDF_STYLES = `
  @page {
    size: A4;
    margin: ${DESTINY_PDF_PAGE_MARGIN_CSS};
  }

  /*
   * Audit #3 — embed a tiny Noto Sans TC subset for zodiac Han glyphs.
   * Latin serif stacks (Georgia / Times via font-serif) often produce tofu in
   * Chromium PDF when CJK fallback fonts are not subset-embedded.
   */
  @font-face {
    font-family: "Noto Sans TC";
    font-style: normal;
    font-weight: 400 700;
    font-display: block;
    src: url("${DESTINY_PDF_CJK_FONT_URL}") format("woff2");
    unicode-range: U+5154, U+672A, U+725B, U+72D7, U+7334, U+77E5, U+7F8A, U+864E, U+86C7, U+8C6C, U+96DE, U+99AC, U+9F20, U+9F8D;
  }

  [data-db-pdf-root],
  [data-db-pdf-root] * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  [data-db-pdf-root] [data-db-pdf-cjk] {
    font-family: "Noto Sans TC", "PingFang TC", "PingFang SC", "Hiragino Sans GB",
      "Microsoft YaHei", "Heiti TC", "STHeiti", sans-serif !important;
    font-style: normal !important;
    font-synthesis: none;
    -webkit-text-fill-color: currentColor;
  }

  /*
   * Audit #8 / #10 — print typography + rhythm tokens (Agent 4).
   * Prefer these roles over one-off text-* sizes inside the Destiny print root.
   */
  /*
   * Exports must always be light mode. PrintResult strips html.dark; these
   * rules reinforce color-scheme + page chrome if a dark class briefly leaks.
   */
  html:has([data-db-pdf-root]),
  html:has([data-db-pdf-root]) body {
    color-scheme: light !important;
    background-color: #ffffff !important;
  }

  [data-db-pdf-root] {
    --db-pdf-ink: #111827;
    --db-pdf-ink-secondary: #4b5563;
    --db-pdf-ink-label: #374151;
    --db-pdf-border: #e5e7eb;
    --db-pdf-border-strong: #d1d5db;
    --db-pdf-card-radius: 12px;
    --db-pdf-gap-pair: 16px;
    --db-pdf-gap-section: 28px;
    --db-pdf-type-section: 22px;
    --db-pdf-type-card-title: 17px;
    --db-pdf-type-panel-title: 20px;
    --db-pdf-type-body: 13px;
    --db-pdf-type-label: 10px;
    --db-pdf-type-eyebrow: 10px;
    color-scheme: light;
    background-color: #ffffff !important;
    color: var(--db-pdf-ink);
  }

  [data-db-pdf-root] .db-pdf-type-section {
    font-size: var(--db-pdf-type-section) !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
    letter-spacing: -0.01em;
    color: var(--db-pdf-ink) !important;
  }

  [data-db-pdf-root] .db-pdf-type-card-title {
    font-size: var(--db-pdf-type-card-title) !important;
    line-height: 1.25 !important;
    font-weight: 700 !important;
    color: var(--db-pdf-ink) !important;
  }

  [data-db-pdf-root] .db-pdf-type-panel-title {
    font-size: var(--db-pdf-type-panel-title) !important;
    line-height: 1.2 !important;
    font-weight: 800 !important;
    color: var(--db-pdf-ink) !important;
  }

  [data-db-pdf-root] .db-pdf-type-body {
    font-size: var(--db-pdf-type-body) !important;
    line-height: 1.55 !important;
    font-weight: 400 !important;
    color: var(--db-pdf-ink-secondary) !important;
  }

  [data-db-pdf-root] .db-pdf-type-label,
  [data-db-pdf-root] .db-pdf-type-eyebrow {
    font-size: var(--db-pdf-type-label) !important;
    line-height: 1.35 !important;
    font-weight: 700 !important;
    letter-spacing: 0.14em !important;
    text-transform: uppercase !important;
    color: var(--db-pdf-ink-label) !important;
  }

  /*
   * Audit #7 — equal-height pairs + odd-last full-span (no dead corner).
   * Pair: two siblings stretch to the same row height.
   * cards-2: 2-column grid; when the last child is alone on a row, span both columns.
   */
  [data-db-pdf-root] [data-db-pdf-grid="pair"] {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: var(--db-pdf-gap-pair) !important;
    align-items: stretch !important;
  }

  [data-db-pdf-root] [data-db-pdf-grid="pair"] > * {
    height: 100%;
    min-height: 100%;
  }

  [data-db-pdf-root] [data-db-pdf-grid="cards-2"] {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: var(--db-pdf-gap-pair) !important;
    align-items: stretch !important;
  }

  [data-db-pdf-root] [data-db-pdf-grid="cards-2"] > * {
    height: 100%;
    min-height: 100%;
  }

  [data-db-pdf-root] [data-db-pdf-grid="cards-2"] > :last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  /*
   * Audit #6 / #15 — flat print cards (border, light fill) over floating shadows.
   * data-db-pdf-card="" → neutral gray border.
   * data-db-pdf-card="accent" → keep component accent border-color (TOP / themed).
   */
  [data-db-pdf-root] [data-db-pdf-card] {
    box-shadow: none !important;
    border-radius: var(--db-pdf-card-radius) !important;
    border-style: solid !important;
    border-width: 1px !important;
  }

  [data-db-pdf-root] [data-db-pdf-card=""] {
    border-color: var(--db-pdf-border) !important;
  }

  [data-db-pdf-root] [data-db-pdf-top-badge] {
    display: inline-flex !important;
    align-items: center !important;
    gap: 0.25rem !important;
    border-radius: 9999px !important;
    padding: 0.15rem 0.55rem !important;
    font-size: 9px !important;
    font-weight: 700 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    line-height: 1.2 !important;
    color: #ffffff !important;
    box-shadow: none !important;
  }

  /*
   * Audit #1 / #11 — print-safe emphasis.
   * Clip-text gradients (bg-clip-text + transparent fill) mis-paint in
   * Chromium/Skia PDF as floating pink/orange boxes beside the glyphs.
   * Force solid brand ink on marked spans for both print-route preview and PDF.
   * Values: secondary (body phrase emphasis) | primary (group/title emphasis).
   */
  [data-db-pdf-root] [data-db-pdf-emphasis] {
    background-image: none !important;
    background-color: transparent !important;
    -webkit-background-clip: border-box !important;
    background-clip: border-box !important;
    font-style: normal !important;
    text-decoration: none !important;
    text-shadow: none !important;
    box-shadow: none !important;
    border: none !important;
    outline: none !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 0 !important;
    display: inline !important;
    vertical-align: baseline !important;
    line-height: inherit !important;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  [data-db-pdf-root] [data-db-pdf-emphasis=""],
  [data-db-pdf-root] [data-db-pdf-emphasis="secondary"] {
    color: #c2410c !important;
    -webkit-text-fill-color: #c2410c !important;
    font-weight: 600 !important;
  }

  [data-db-pdf-root] [data-db-pdf-emphasis="primary"] {
    color: #8b1167 !important;
    -webkit-text-fill-color: #8b1167 !important;
    font-weight: 700 !important;
  }

  @media screen {
    [data-db-pdf-root].print-root {
      max-width: 860px;
      margin: 0 auto;
      padding: 24px 32px 48px;
      box-sizing: border-box;
      background: #ffffff;
      color: #111827;
    }

    [data-db-pdf-root] .db-pdf-running-header,
    [data-db-pdf-root] .db-pdf-running-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #6b7280;
      border-color: #e5e7eb;
    }

    [data-db-pdf-root] .db-pdf-running-header {
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
    }

    [data-db-pdf-root] .db-pdf-running-footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
    }
  }

  @media print {
    @page {
      size: A4;
      margin: ${DESTINY_PDF_PAGE_MARGIN_CSS};
    }

    html,
    body {
      background: #ffffff !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    [data-db-pdf-root].print-root {
      background: #ffffff !important;
      color: var(--db-pdf-ink) !important;
      max-width: none !important;
      width: 100% !important;
      margin: 0 !important;
      /*
       * Page inset comes from @page + Puppeteer margins (header/footer band).
       * Keep root padding at 0 so continued pages rely on that shared clearance.
       */
      padding: 0 !important;
      box-sizing: border-box !important;
      min-height: 0 !important;
      overflow: visible !important;
    }

    /* Screen-preview chrome — Puppeteer header/footer owns PDF running chrome */
    [data-db-pdf-root] .db-pdf-running-header,
    [data-db-pdf-root] .db-pdf-running-footer {
      display: none !important;
    }

    /*
     * Cover sheet: fill the printable area and vertically center the card so
     * the opener is not a tiny orphan floating at the top of a blank A4.
     * @page already reserves the header/footer band — keep inner padding modest.
     */
    [data-db-pdf-root] .print-cover-page {
      page-break-after: always;
      break-after: page;
      min-height: 220mm;
      display: flex !important;
      flex-direction: column !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      max-width: none !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      padding: 8mm 4mm !important;
    }

    .print-hide,
    [data-pdf-print-hide] {
      display: none !important;
    }

    /*
     * Audit #4 / #12 — decorative watermarks / huge glyphs fight body text.
     * Keep on-screen Result; hide for print/PDF.
     */
    [data-db-pdf-root] [data-pdf-decorative-watermark] {
      display: none !important;
      visibility: hidden !important;
    }

    /* Utility break helpers for Agents 2+ */
    .print-page-break {
      break-before: page;
      page-break-before: always;
    }

    .print-avoid-break,
    [data-pdf-keep-together] {
      break-inside: avoid;
      page-break-inside: avoid;
      -webkit-column-break-inside: avoid;
    }

    [data-pdf-page-break-before] {
      break-before: page;
      page-break-before: always;
    }

    /*
     * Audit #2 / #5 / #13 / #14 — card-sized keep-together only.
     * Do NOT put break-inside:avoid on entire multi-page chapters.
     */
    [data-db-pdf-root] [data-pdf-chapter] {
      break-inside: auto;
      page-break-inside: auto;
    }

    /*
     * Section banners (01/02/03…): never slice mid-banner; keep with following
     * payload when Chromium can (prevents banner-only near-blank sheets).
     */
    [data-db-pdf-root] [data-pdf-section-opener] {
      break-inside: avoid;
      page-break-inside: avoid;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    /*
     * Audit #6 — flatten web-card chrome for print (no floating drop shadows).
     * Covers Tailwind shadow utilities and arbitrary shadow-[…] / colored shadows.
     */
    [data-db-pdf-root] .shadow-sm,
    [data-db-pdf-root] .shadow,
    [data-db-pdf-root] .shadow-md,
    [data-db-pdf-root] .shadow-lg,
    [data-db-pdf-root] .shadow-xl,
    [data-db-pdf-root] .shadow-2xl,
    [data-db-pdf-root] .shadow-inner,
    [data-db-pdf-root] [class*="shadow-["],
    [data-db-pdf-root] [class*="shadow-orange"],
    [data-db-pdf-root] [class*="shadow-brand"],
    [data-db-pdf-root] [class*="shadow-black"] {
      box-shadow: none !important;
    }

    /*
     * Audit #8 — steadier vertical rhythm between major blocks.
     */
    [data-db-pdf-root] [data-pdf-chapter] {
      margin-bottom: var(--db-pdf-gap-section) !important;
    }

    [data-db-pdf-root] [data-pdf-section-opener] {
      margin-bottom: 18px !important;
    }

    /*
     * Audit #10 — boost faint secondary labels so body hierarchy stays readable.
     */
    [data-db-pdf-root] .text-theme-fg-secondary,
    [data-db-pdf-root] .text-gray-400,
    [data-db-pdf-root] .text-gray-500,
    [data-db-pdf-root] .text-slate-500 {
      color: var(--db-pdf-ink-label) !important;
    }

    /*
     * Audit #15 — unify TOP badge geometry when components mark the attribute.
     */
    [data-db-pdf-root] [data-db-pdf-top-badge] {
      box-shadow: none !important;
    }

    [data-db-pdf-root] img,
    [data-db-pdf-root] svg,
    [data-db-pdf-root] table {
      max-width: 100% !important;
    }

    /*
     * Sticky / fixed screen affordances must not pin in the PDF flow.
     * Do not target .db-pdf-* chrome here (already hidden in print).
     */
    [data-db-pdf-root] .sticky,
    [data-db-pdf-root] [class*="sticky"] {
      position: static !important;
      top: auto !important;
      bottom: auto !important;
    }
  }
`;
